import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { ASSETS, api } from "@/lib/api";

const useNoIndex = () => {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
};

const Items = ({ items }) => (
  <ul className="divide-y divide-white/10">
    {items.map((it, i) => (
      <li key={i} data-testid={`table-menu-item-${i}`} className="py-4 flex justify-between gap-4">
        <div>
          <div className="font-display uppercase text-lg tracking-tight">{it.name}</div>
          {it.desc && <div className="text-sm text-white/60 leading-relaxed">{it.desc}</div>}
        </div>
        <div className="font-display text-lg text-[var(--trio-accent)] whitespace-nowrap">{it.price}</div>
      </li>
    ))}
  </ul>
);

export default function TableMenu() {
  const { code } = useParams();
  const [state, setState] = useState({ loading: true });
  const [active, setActive] = useState(0);
  useNoIndex();

  useEffect(() => {
    api.get(`/menus/table/${code}`)
      .then(({ data }) => setState({ data }))
      .catch(() => setState({ expired: true }));
  }, [code]);

  if (state.loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[var(--trio-primary)]" /></div>;
  }

  if (state.expired) {
    return (
      <div data-testid="table-menu-expired" className="min-h-screen bg-black text-white flex flex-col items-center justify-center text-center px-6">
        <Lock className="w-10 h-10 text-[var(--trio-primary)] mb-4" />
        <h1 className="font-display uppercase text-3xl tracking-tight mb-2">This Menu Link Expired</h1>
        <p className="text-sm text-white/60">Ask your server for a fresh QR code.</p>
      </div>
    );
  }

  const { table, brunch } = state.data;
  const brunchSections = brunch.sections.filter((s) => s.items.length).map((s) => ({ ...s, brunch: true }));
  const sections = [...brunchSections, ...table.sections.filter((s) => s.items.length)];
  const current = sections[active];

  return (
    <div data-testid="table-menu-page" className="min-h-screen bg-black text-white" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <header className="px-6 pt-10 pb-6 text-center">
        <img src={ASSETS.logo} alt="Trio" className="h-12 w-auto mx-auto mb-4 invert" />
        <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--trio-primary)] font-bold">Eat. Drink. Vibe.</div>
        <h1 className="font-display uppercase text-4xl tracking-tight mt-2">{table.title || "Table Menu"}</h1>
        {table.note && <p data-testid="table-menu-note" className="text-sm text-white/60 mt-2">{table.note}</p>}
      </header>
      {sections.length === 0 ? (
        <p className="text-center text-white/60 px-6">Menu coming soon. Ask your server.</p>
      ) : (
        <>
          <nav className="sticky top-0 z-10 bg-black/95 backdrop-blur border-y border-white/10 overflow-x-auto">
            <div className="flex gap-1 px-4 min-w-max mx-auto justify-center">
              {sections.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`table-menu-tab-${i}`}
                  className={`px-4 py-3 text-xs uppercase tracking-widest border-b-2 transition-colors ${
                    i === active ? "border-[var(--trio-primary)] text-[var(--trio-primary)]" : "border-transparent text-white/60"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </nav>
          <main className="max-w-2xl mx-auto px-6 py-8">
            {current.brunch && brunch.note && (
              <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold mb-2">{brunch.note}</div>
            )}
            <h2 data-testid="table-menu-section-title" className="font-display uppercase text-3xl tracking-tight mb-2">{current.name}</h2>
            <Items items={current.items} />
          </main>
        </>
      )}
    </div>
  );
}
